/**
 * Pooled floating count-delta numbers for every N change (gates, losses, absorb).
 *
 * W5 gateBreak already pops gate op text at the gate; this pool covers combat
 * drains, absorb, and any other delta the shell/renderer notifies.
 * Presentation only — sim already applied the math.
 */
import * as THREE from "three";
/** Max simultaneous floating count sprites (pooled). */
export const COUNT_POPUP_POOL = 16;
/** Float lifetime (seconds). */
export const COUNT_POPUP_LIFE = 0.9;
function kindColor(kind) {
    switch (kind) {
        case "gain":
        case "gate":
            return 0x34d399;
        case "loss":
            return 0xf87171;
        case "absorb":
            return 0x93c5fd;
        default:
            return 0xf8fafc;
    }
}
function formatDelta(delta) {
    const n = Math.trunc(delta);
    if (n > 0)
        return `+${n}`;
    if (n < 0)
        return `${n}`; // already has minus
    return "0";
}
function hexCss(hex) {
    return `#${hex.toString(16).padStart(6, "0")}`;
}
function makeCountTexture(text, colorHex, size = 256) {
    if (typeof document === "undefined") {
        const data = new Uint8Array([255, 255, 255, 255]);
        const tex = new THREE.DataTexture(data, 1, 1);
        tex.needsUpdate = true;
        return tex;
    }
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.clearRect(0, 0, size, size);
        ctx.fillStyle = "rgba(15, 23, 42, 0.55)";
        const pad = size * 0.08;
        roundRect(ctx, pad, size * 0.28, size - pad * 2, size * 0.44, size * 0.08);
        ctx.fill();
        ctx.font = `bold ${Math.floor(size * 0.36)}px system-ui,Segoe UI,sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.lineWidth = Math.max(4, size * 0.04);
        ctx.strokeStyle = "rgba(0,0,0,0.75)";
        ctx.strokeText(text, size / 2, size / 2);
        ctx.fillStyle = colorHex;
        ctx.fillText(text, size / 2, size / 2);
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}
function roundRect(ctx, x, y, w, h, r) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
}
/**
 * Create pooled floating count-popup FX for every count delta.
 */
export function createCountPopupFx() {
    const group = new THREE.Group();
    group.name = "countPopupFx";
    const popups = [];
    for (let i = 0; i < COUNT_POPUP_POOL; i++) {
        const material = new THREE.SpriteMaterial({
            transparent: true,
            depthWrite: false,
            opacity: 0,
        });
        const sprite = new THREE.Sprite(material);
        sprite.name = `countPopup:${i}`;
        sprite.visible = false;
        sprite.scale.set(1.8, 1.8, 1);
        group.add(sprite);
        popups.push({
            active: false,
            age: 0,
            life: COUNT_POPUP_LIFE,
            x: 0,
            y: 0,
            z: 0,
            sprite,
            material,
            texture: null,
        });
    }
    let cursor = 0;
    const spawn = (input) => {
        const kind = input.kind ??
            (typeof input.delta === "number"
                ? input.delta < 0
                    ? "loss"
                    : input.delta > 0
                        ? "gain"
                        : "neutral"
                : "neutral");
        const color = input.color ?? kindColor(kind);
        const text = input.text ??
            (typeof input.delta === "number" ? formatDelta(input.delta) : "?");
        if (!text || text === "0" || text === "+0") {
            // Still allow explicit zero for debugging; skip empty
            if (!input.text && (!input.delta || input.delta === 0))
                return;
        }
        const p = popups[cursor];
        cursor = (cursor + 1) % COUNT_POPUP_POOL;
        if (p.texture) {
            p.texture.dispose();
            p.texture = null;
        }
        const tex = makeCountTexture(text, hexCss(color), 256);
        p.texture = tex;
        p.material.map = tex;
        p.material.opacity = 1;
        p.material.needsUpdate = true;
        p.active = true;
        p.age = 0;
        p.life = COUNT_POPUP_LIFE;
        p.x = input.x + (cursor % 3) * 0.15 - 0.15;
        p.y = (input.y ?? 1.4) + (cursor % 2) * 0.2;
        p.z = input.z;
        p.sprite.position.set(p.x, p.y, p.z);
        p.sprite.scale.set(2.0, 2.0, 1);
        p.sprite.visible = true;
        group.visible = true;
    };
    const update = (dtSeconds) => {
        const dt = Math.max(0, Math.min(0.05, dtSeconds));
        if (dt <= 0)
            return;
        let any = false;
        for (let i = 0; i < COUNT_POPUP_POOL; i++) {
            const p = popups[i];
            if (!p.active)
                continue;
            p.age += dt;
            if (p.age >= p.life) {
                p.active = false;
                p.sprite.visible = false;
                p.material.opacity = 0;
                continue;
            }
            any = true;
            const u = p.age / p.life;
            p.y += (1.6 + (1 - u) * 0.7) * dt;
            p.sprite.position.set(p.x, p.y, p.z);
            const fade = u < 0.12 ? u / 0.12 : 1 - (u - 0.12) / 0.88;
            p.material.opacity = Math.max(0, Math.min(1, fade));
            const sc = 2.0 + u * 0.5;
            p.sprite.scale.set(sc, sc, 1);
        }
        group.visible = any;
    };
    const reset = () => {
        for (const p of popups) {
            p.active = false;
            p.sprite.visible = false;
            p.material.opacity = 0;
            if (p.texture) {
                p.texture.dispose();
                p.texture = null;
                p.material.map = null;
            }
        }
        group.visible = false;
    };
    const dispose = () => {
        reset();
        for (const p of popups) {
            p.material.dispose();
        }
        group.clear();
    };
    return { group, spawn, update, reset, dispose };
}
//# sourceMappingURL=countPopup.js.map